import { useEffect, useMemo, useState } from "react";
import {
  FaChartLine,
  FaChevronDown,
  FaChevronRight,
  FaCog,
  FaHome,
  FaQuestionCircle,
} from "react-icons/fa";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { Scrollbars } from "rc-scrollbars";

// import images
import Logo from "./../../assets/images/logo.png";
import Collapse from "./../../assets/images/hamburger.png";
import { PiPulseLight, PiSignOut } from "react-icons/pi";
import { getAuthenticationDetailForInvitation } from "../../services/authService";
import { useSelector } from "react-redux";

const SideBar = () => {
  const location = useLocation();
  const [collapseMenu, setCollapseMenu] = useState(false);
  const isMobile = useMediaQuery({ query: "(max-width: 992px)" });
  const [invitationDetails, setInvitationDetails] = useState({
    company_unique_id: "",
    invitation_password: "",
  });
  const { company_master_account, user_id } = useSelector(
    (state) => state.auth
  );

  const init = async () => {
    const res = await getAuthenticationDetailForInvitation();
    if (res.status === "success") {
      setInvitationDetails(res.data);
    }
  };

  const invitationURL = useMemo(
    () =>
      `https://${company_master_account?.company_url}/welcome/e/signup?company_unique_id=${invitationDetails.company_unique_id}&invitation_password=${invitationDetails.invitation_password}`,
    [invitationDetails, company_master_account]
  );

  useEffect(() => {
    init();
  }, []);

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(invitationURL)
      .then(() => {
        console.log("Copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  return (
    <div className="vh-100 position-relative">
      <div className="d-flex w-100 align-items-center justify-content-between py-4 my-lg-3 d-lg-flex d-none">
        {!collapseMenu && (
          <Link to="/" className="ps-3">
            <img src={Logo} alt="logo" />
          </Link>
        )}
        <Link
          className="ps-3"
          to="#"
          onClick={(e) => {
            e.preventDefault();
            setCollapseMenu(!collapseMenu);
          }}
        >
          <img src={Collapse} alt="hamburger" />
        </Link>
      </div>

      <Scrollbars
        autoHide
        autoHeight
        autoHeightMax="calc(100vh - 215px)"
        renderThumbVertical={({ style, ...props }) => (
          <div
            {...props}
            style={{
              ...style,
              backgroundColor: "#CCC", // Change color
              borderRadius: "4px",
            }}
          />
        )}
      >
        <Sidebar
          className="border-0"
          rootStyles={{
            "& .ps-sidebar-container, &.ps-submenu-content": {
              backgroundColor: "transparent !important",
              color: "#CCC",
              fontSize: "1rem",
            },
            "& .ps-sidebar-root": {
              border: "none !important", // This removes the border
            },
            "&.ps-collapsed": {
              width: "50px",
              minWidth: "50px",
              "& .ps-submenu-content .ps-menu-button": {
                paddingLeft: "30px !important",
                paddingRight: "10px",
              },
            },
          }}
          breakPoint="sm"
          collapsed={isMobile || collapseMenu}
        >
          <Menu
            menuItemStyles={{
              button: {
                [`&.ps-active, &:hover`]: {
                  borderLeft: "3px solid #00C8A0", // Fixed border style
                  background:
                    "linear-gradient(90.01deg, rgba(0, 200, 160, 0.4) 0.01%, rgba(0, 200, 160, 0) 52.67%)",
                  color: "#FFF",
                },
              },
            }}
            renderExpandIcon={({ open }) =>
              open ? (
                <FaChevronDown className="text-sm" />
              ) : (
                <FaChevronRight className="text-sm" />
              )
            }
          >
            {company_master_account?.is_analytics === 1 && (
              <SubMenu
                label="Tools"
                icon={<PiPulseLight />}
                className="bg-transparent"
                rootStyles={{
                  "& .ps-submenu-content": {
                    backgroundColor: "#1A5549",
                    color: "#CCC",
                    fontSize: "1rem",
                    "& .ps-menu-button": {
                      paddingLeft: "65px",
                      "&.ps-active, &:hover": {
                        color: "#00C8A0",
                        background: "transparent",
                        border: "none",
                      },
                    },
                  },
                }}
                active={location.pathname.startsWith("/pulse/")}
                defaultOpen={location.pathname.startsWith("/pulse/")}
              >
                <MenuItem
                  active={location.pathname === "/pulse/mindfulness"}
                  component={
                    <Link to="/pulse/mindfulness" className="step-1" />
                  }
                >
                  Mindfulness
                </MenuItem>
                {company_master_account?.supportfit && (
                  <MenuItem
                    active={location.pathname === "/pulse/physical"}
                    component={<Link to="/pulse/physical" />}
                  >
                    Physical
                  </MenuItem>
                )}
                {company_master_account?.nixy && (
                  <MenuItem
                    active={location.pathname === "/pulse/women-wellness"}
                    component={<Link to="/pulse/women-wellness" />}
                  >
                    Women's Wellness
                  </MenuItem>
                )}
                {company_master_account?.only_heartbeat === 1 && (
                  <MenuItem
                    active={location.pathname.startsWith("/pulse/heartbeat")}
                    component={<Link to="/pulse/heartbeat" />}
                  >
                    Heartbeat
                  </MenuItem>
                )}
              </SubMenu>
            )}

            <MenuItem
              icon={<FaChartLine />}
              active={location.pathname === "/report"}
              component={<Link to="/report" />}
            >
              Monitor
            </MenuItem>
            <MenuItem
              icon={<FaHome />}
              active={location.pathname === "/leaders-hub"}
              component={<Link to="/leaders-hub" />}
            >
              Leaders Hub
            </MenuItem>
            <SubMenu
              label="Settings"
              icon={<FaCog />}
              className="bg-transparent"
              rootStyles={{
                "& .ps-submenu-content": {
                  backgroundColor: "#1A5549",
                  color: "#CCC",
                  fontSize: "1rem",
                  "& .ps-menu-button": {
                    paddingLeft: "65px",
                    "&.ps-active, &:hover": {
                      color: "#00C8A0",
                      background: "transparent",
                      border: "none",
                    },
                  },
                },
              }}
              defaultOpen={location.pathname.startsWith("/settings/")}
            >
              <MenuItem
                active={location.pathname === "/settings/profile-settings"}
                component={<Link to="/settings/profile-settings" />}
              >
                Profile Settings
              </MenuItem>
              {Number(user_id) === Number(company_master_account?.id) && (
                <MenuItem
                  active={location.pathname === "/settings/manager-upload"}
                  component={<Link to="/settings/manager-upload" />}
                >
                  Manager Upload
                </MenuItem>
              )}
              <MenuItem
                active={location.pathname === "/settings/employee-upload"}
                component={<Link to="/settings/employee-upload" />}
              >
                Employee Upload
              </MenuItem>
            </SubMenu>
            <MenuItem
              icon={<FaQuestionCircle />}
              active={location.pathname === "/support"}
              component={<Link to="/support" />}
            >
              Help & Support
            </MenuItem>
            <MenuItem
              icon={<PiSignOut />}
              active={location.pathname === "/logout"}
              component={<Link to="/logout" />}
            >
              Logout
            </MenuItem>
          </Menu>
        </Sidebar>
      </Scrollbars>

      {!collapseMenu && !isMobile && (
        <div className="px-4 py-4 position-absolute bottom-0 side-menu-copy-button-container">
          <p className="text-light fw-semibold mb-0">
            Invite users to join platform
          </p>
          <p className="text-white text-truncate mb-1">{invitationURL}</p>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={copyToClipboard}
          >
            Copy URL
          </button>
        </div>
      )}
    </div>
  );
};

export default SideBar;
